/**
 * Navigation Bar Component
 *
 * A responsive navigation header that adapts to different screen sizes and user authentication states.
 * This component provides the main navigation interface for the application, including:
 *
 * - Responsive design with desktop and mobile layouts
 * - User authentication state awareness (logged in vs. logged out)
 * - User profile menu with avatar and dropdown options
 * - Theme switching functionality
 * - Language switching functionality
 * - Mobile-friendly navigation drawer
 *
 * The component handles different states:
 * - Loading state with skeleton placeholders
 * - Authenticated state with user profile information
 * - Unauthenticated state with sign in/sign up buttons
 */
import { CogIcon, HomeIcon, LogOutIcon, MenuIcon, ArrowRightLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { Link, useFetcher } from "react-router";

import { logout } from "~/features/auth/api/auth-api";

import { getAuthToken } from "../lib/api";
import LangSwitcher from "./lang-switcher";
import ThemeSwitcher from "./theme-switcher";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";
import {
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from "./ui/sheet";

/* ── 환경 전환 설정 ── */
const DOMAIN_CONFIG = {
  production: { domain: "score.ohmyplay.com", label: "운영", color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
  hotfix: { domain: "hotfixscore.ohmyplay.com", label: "Hotfix", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  development: { domain: "devscore.ohmyplay.com", label: "개발", color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  local: { domain: "localhost", label: "로컬", color: "#a855f7", bg: "rgba(168,85,247,0.12)" },
} as const;
type EnvType = keyof typeof DOMAIN_CONFIG;

function detectEnvironment(): EnvType {
  if (typeof window === "undefined") return "production";
  const h = window.location.hostname;
  if (h.startsWith("hotfixscore")) return "hotfix";
  if (h.startsWith("devscore")) return "development";
  if (h === "localhost" || h === "127.0.0.1") return "local";
  return "production";
}

function switchEnvironment(targetEnv: EnvType) {
  if (typeof window === "undefined") return;
  const target = DOMAIN_CONFIG[targetEnv];
  const path = window.location.pathname + window.location.search;
  const protocol = targetEnv === "local" ? "http:" : "https:";
  const port = targetEnv === "local" ? `:${window.location.port || "5173"}` : "";
  window.location.href = `${protocol}//${target.domain}${port}${path}`;
}

/** 타이틀 옆 환경 라벨 + 클릭 시 전환 팝오버 */
function EnvironmentBadge() {
  const [env, setEnv] = useState<EnvType>("production");
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => setEnv(detectEnvironment()), []);

  // 외부 클릭 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const cfg = DOMAIN_CONFIG[env];
  const targetEnv: EnvType = env === "hotfix" ? "production" : "hotfix";
  const targetCfg = DOMAIN_CONFIG[targetEnv];

  return (
    <div ref={popRef} style={{ position: "relative", display: "inline-flex" }}>
      {/* 라벨 배지 */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px", borderRadius: 6,
          fontSize: 11, fontWeight: 600,
          color: cfg.color, background: cfg.bg,
          border: "none", cursor: "pointer",
          lineHeight: "18px",
          transition: "opacity 0.15s",
        }}
      >
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: cfg.color, display: "inline-block",
        }} />
        {cfg.label}
      </button>

      {/* 팝오버 */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0,
          background: "white", borderRadius: 10,
          boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
          padding: 12, minWidth: 220, zIndex: 999,
          border: "1px solid rgba(0,0,0,0.08)",
        }}>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
            현재: {cfg.domain}
          </div>
          <button
            type="button"
            onClick={() => { switchEnvironment(targetEnv); setOpen(false); }}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              width: "100%", padding: "8px 12px", borderRadius: 8,
              background: targetCfg.bg, border: `1px solid ${targetCfg.color}30`,
              color: targetCfg.color, fontSize: 13, fontWeight: 600,
              cursor: "pointer", transition: "background 0.15s",
            }}
          >
            <ArrowRightLeft size={14} />
            {targetCfg.label} 환경으로 전환
          </button>
          <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 6 }}>
            {targetCfg.domain}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * UserMenu Component
 *
 * Displays the authenticated user's profile menu with avatar and dropdown options.
 * This component is shown in the navigation bar when a user is logged in and provides
 * quick access to user-specific actions and information.
 *
 * Features:
 * - Avatar display with image or fallback initials
 * - User name and email display
 * - Quick navigation to dashboard
 * - Logout functionality
 *
 * @param name - The user's display name
 * @param email - The user's email address (optional)
 * @param avatarUrl - URL to the user's avatar image (optional)
 * @returns A dropdown menu component with user information and actions
 */
function UserMenu({
  name,
}: {
  name: string | undefined;
}) {
  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    logout();
    // logout 함수 내에서 이벤트를 발생시키므로 여기서는 상태 업데이트 불필요
  };
  return (
    <DropdownMenu>
      {/* Avatar as the dropdown trigger */}
      <DropdownMenuTrigger asChild>
        <Avatar className="size-8 cursor-pointer rounded-lg">
          <AvatarImage src={undefined} />
          <AvatarFallback>{name?.slice(0, 2)}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>

      {/* Dropdown content with user info and actions */}
      <DropdownMenuContent className="w-56">
        {/* User information display */}
        <DropdownMenuLabel className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate font-semibold">{name}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Dashboard link */}
        {/* <DropdownMenuItem asChild>
          <SheetClose asChild>
            <Link to="/dashboard" viewTransition>
              <HomeIcon className="size-4" />
              Dashboard
            </Link>
          </SheetClose>
        </DropdownMenuItem> */}

        {/* Logout link */}
        <DropdownMenuItem asChild>
          <SheetClose asChild>
            <Link to="/logout" viewTransition onClick={handleLogout}>
              <LogOutIcon className="size-4" />
              Log out
            </Link>
          </SheetClose>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * AuthButtons Component
 *
 * Displays authentication buttons (Sign in and Sign up) for unauthenticated users.
 * This component is shown in the navigation bar when no user is logged in and provides
 * quick access to authentication screens.
 *
 * Features:
 * - Sign in button with ghost styling (less prominent)
 * - Sign up button with default styling (more prominent)
 * - View transitions for smooth navigation to auth screens
 * - Compatible with mobile navigation drawer (SheetClose integration)
 *
 * @returns Fragment containing sign in and sign up buttons
 */
function AuthButtons() {
  return (
    <>
      {/* Sign in button (less prominent) */}
      <Button variant="ghost" asChild>
        <SheetClose asChild>
          <Link to="/login" viewTransition>
            Sign in
          </Link>
        </SheetClose>
      </Button>
    </>
  );
}

/**
 * Actions Component
 *
 * Displays utility actions and settings in the navigation bar, including:
 * - Debug/settings dropdown menu with links to monitoring tools
 * - Theme switcher for toggling between light and dark mode
 * - Language switcher for changing the application language
 *
 * This component is shown in the navigation bar for all users regardless of
 * authentication state and provides access to application-wide settings and tools.
 *
 * @returns Fragment containing settings dropdown, theme switcher, and language switcher
 */
function Actions() {
  return (
    <>
      {/* <DropdownMenu>
        <DropdownMenuTrigger asChild className="cursor-pointer">
          <Button variant="ghost" size="icon">
            <CogIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <SheetClose asChild>
              <Link to="/debug/sentry" viewTransition>
                Sentry
              </Link>
            </SheetClose>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <SheetClose asChild>
              <Link to="/debug/analytics" viewTransition>
                Google Tag
              </Link>
            </SheetClose>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu> */}
      {/* Theme switcher component (light/dark mode) */}
      <ThemeSwitcher />

      {/* Language switcher component */}
      <LangSwitcher />
    </>
  );
}

/**
 * NavigationBar Component
 *
 * The main navigation header for the application that adapts to different screen sizes
 * and user authentication states. This component serves as the primary navigation
 * interface and combines several sub-components to create a complete navigation experience.
 *
 * Features:
 * - Responsive design with desktop navigation and mobile drawer
 * - Application branding with localized title
 * - Main navigation links (Blog, Contact, Payments)
 * - User authentication state handling (loading, authenticated, unauthenticated)
 * - User profile menu with avatar for authenticated users
 * - Sign in/sign up buttons for unauthenticated users
 * - Theme and language switching options
 *
 * @param name - The authenticated user's name (if available)
 * @param email - The authenticated user's email (if available)
 * @param avatarUrl - The authenticated user's avatar URL (if available)
 * @param loading - Boolean indicating if the auth state is still loading
 * @returns The complete navigation bar component
 */
export function NavigationBar({
  name,
  email,
  avatarUrl,
  loading,
  isLoggedIn,
}: {
  name?: string;
  email?: string;
  avatarUrl?: string | null;
  loading: boolean;
  isLoggedIn: boolean;
}) {
  // Get translation function for internationalization
  const { t } = useTranslation();
  return (
    <nav
      className={
        "mx-auto flex h-16 w-full items-center justify-between border-b px-5 shadow-xs backdrop-blur-lg transition-opacity md:px-10"
      }
    >
      <div className="mx-auto flex h-full w-full max-w-screen-2xl items-center justify-between py-3">
        {/* Application logo/title with link to home + 환경 라벨 */}
        <div className="flex items-center gap-2">
          <Link to="/">
            <h1 className="text-lg font-extrabold">{t("home.title")}</h1>
          </Link>
          <EnvironmentBadge />
        </div>

        {/* Desktop navigation menu (hidden on mobile) */}
        <div className="hidden h-full items-center gap-5 md:flex">
          {/* Main navigation links */}
          <Link
            to="/docs"
            viewTransition
            className="text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            사용 설명서
          </Link>

          <Separator orientation="vertical" />

          {/* Settings, theme switcher, and language switcher */}
          <Actions />

          <Separator orientation="vertical" />

          {/* Conditional rendering based on authentication state */}
          {loading ? (
            // Loading state with skeleton placeholder
            <div className="flex items-center">
              <div className="bg-muted-foreground/20 size-8 animate-pulse rounded-lg" />
            </div>
          ) : (
            <>
              {isLoggedIn ? (
                // Authenticated state with user menu
                <UserMenu name={name} />
              ) : (
                // Unauthenticated state with auth buttons
                <AuthButtons />
              )}
            </>
          )}
        </div>

        {/* Mobile menu trigger (hidden on desktop) */}
        <SheetTrigger className="size-6 md:hidden">
          <MenuIcon />
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetClose asChild>
              <Link to="/blog">Blog</Link>
            </SheetClose>
            <SheetClose asChild>
              <Link to="/contact">Contact</Link>
            </SheetClose>
            <SheetClose asChild>
              <Link to="/payments/checkout">Payments</Link>
            </SheetClose>
          </SheetHeader>
          {loading ? (
            <div className="flex items-center">
              <div className="bg-muted-foreground h-4 w-24 animate-pulse rounded-full" />
            </div>
          ) : (
            <SheetFooter>
              {isLoggedIn ? (
                <div className="grid grid-cols-3">
                  <div className="col-span-2 flex w-full justify-between">
                    <Actions />
                  </div>
                  <div className="flex justify-end">
                    <UserMenu name={name} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  <div className="flex justify-between">
                    <Actions />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <AuthButtons />
                  </div>
                </div>
              )}
            </SheetFooter>
          )}
        </SheetContent>
      </div>
    </nav>
  );
}
